import React, { useMemo } from 'react';
import {
    AddCircledIcon,
    Button,
    Callout,
    FlexContainer,
    Icon,
    IconButton,
    Link,
    TrashIcon,
} from '@placer-ui/components';
import styles from './property-form.module.scss';
import { ControlledPropertyForm } from './controlled-property-form';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { PropertyFormValues } from 'features/my-zone/support-requests/components/support-requests-modals/modals/request-new-location/request-property/types';
import { useSendSupportSplunkEvent } from 'features/my-zone/support-requests/hooks/use-send-support-splunk-event';
import { useOpenSupportRequest } from 'features/my-zone/support-requests/components/support-requests-modals/support-modal-manager';
import { getFeatureFlags } from 'core/flow-control';
import {
    SupportOpportunityUrlInput,
    SupportSalesEmailInput,
} from 'features/my-zone/support-requests/components/support-requests-modals/components/inputs/controlled-support-sales-email-inputs/support-sales-email-inputs';

const emptyPropertyForm = {
    name: '',
    address: '',
    zip_code: '',
};

export const PropertiesForm = () => {
    const formController = useFormContext<PropertyFormValues>();
    const propertiesArray = useFieldArray({
        control: formController.control,
        name: 'properties',
    });
    const { openSupportRequest } = useOpenSupportRequest();
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { enable_request_properties_sales_flow_ff } = getFeatureFlags();

    const { id, ...property } = propertiesArray.fields[0] ?? {};

    const MAX_PROPERTIES = enable_request_properties_sales_flow_ff ? 10 : 20;

    const disableRemoveButton = useMemo(
        () =>
            propertiesArray.fields.length === 1 &&
            JSON.stringify(property) === JSON.stringify(emptyPropertyForm),
        [propertiesArray.fields.length, property],
    );

    const handleAdvanceRequestOpen = () => {
        sendSplunkEvent({
            action: 'button-click',
            value: 'advancedPropertyPolygon',
        });

        openSupportRequest({ flowType: 'advancedPropertyPolygon' });
    };

    return (
        <div className={styles.propertiesFormContainer} data-testid="properties-form">
            <div className={styles.propertiesForm}>
                {enable_request_properties_sales_flow_ff && (
                    <FlexContainer gap={16}>
                        <SupportSalesEmailInput />
                        <SupportOpportunityUrlInput />
                    </FlexContainer>
                )}
                {propertiesArray.fields.map((property, index) => (
                    <div className={styles.formInputsContainer} key={property.id}>
                        <ControlledPropertyForm control={formController.control} index={index} />
                        <IconButton
                            disabled={disableRemoveButton}
                            Icon={<TrashIcon />}
                            variant="secondary-gray"
                            testId={`remove-property-button-${index}`}
                            onClick={() => {
                                if (propertiesArray.fields.length === 1) {
                                    propertiesArray.append(emptyPropertyForm);
                                }
                                propertiesArray.remove(index);
                            }}
                            aria-label="Remove Property"
                        />
                    </div>
                ))}
                {propertiesArray.fields.length < MAX_PROPERTIES && (
                    <Button
                        LeftIcon={<AddCircledIcon />}
                        variant="secondary-blue"
                        onClick={() => propertiesArray.append(emptyPropertyForm)}
                        testId="add-property-button"
                    >
                        Add Property
                    </Button>
                )}
            </div>
            <div className={styles.callout}>
                <Callout
                    FreeTextIcon={<Icon type="Info" />}
                    text="For advanced requests requiring SHP files, Site Plans, or other detailed formats"
                    CTA={
                        <Link
                            onClick={handleAdvanceRequestOpen}
                            testId="request-property-advance-link"
                        >
                            click here
                        </Link>
                    }
                />
            </div>
        </div>
    );
};
